import { type FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useParams } from "react-router";

import { isNumberFormat, toSafeInt } from "widgets/utils";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { useURLViewID } from "../../app-utils/hooks/useUrl.js";
import { type IJobState, getFullJobResult, loadFullJobResult } from "../../redux/modules/job.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import { BlastResultAlignmentsView } from "./BlastResultAlignmentsView.js";
import { BlastResultHitDetailsView } from "./BlastResultHitDetailsView.js";
import { BlastResultHitsTableView } from "./BlastResultHitsTableView.js";
import { BlastResultHspsTableView } from "./BlastResultHspsTableView.js";
import { BlastResultSummaryView } from "./BlastResultSummaryView.js";

const BlastResultDetailPage: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();

  const { jobId, queryId } = useParams<"jobId" | "queryId">();

  useEffect(() => {
    if (isNumberFormat(jobId) && isNumberFormat(queryId)) {
      void dispatch(loadFullJobResult(toSafeInt(jobId), toSafeInt(queryId)));
    }
  }, [dispatch, jobId, queryId]);

  const { job, isLoading, jobResults, isResultLoading } = useSelector<IAppState, IJobState>((state) => state.job);
  const viewId = useURLViewID();

  const commonProps = {
    job,
    isLoading: isLoading || isResultLoading,
    jobResult: getFullJobResult(jobResults, toSafeInt(queryId)),
    queryId: toSafeInt(queryId),
  };

  const noHitsFound: boolean =
    !commonProps.isLoading && commonProps.jobResult && !commonProps.jobResult.blastHits?.length;

  if (noHitsFound) {
    return (
      <div className={styleLayout.page}>
        <div className={styleLayout.page_container_middle}>
          <ContentPlaceholder text="No hits found" />
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path={"summary/*"} element={<BlastResultSummaryView {...commonProps} />}></Route>
      <Route path={"hits/*"} element={<BlastResultHitsTableView {...commonProps} viewId={viewId} />}></Route>
      <Route path={"hsps/*"} element={<BlastResultHspsTableView {...commonProps} viewId={viewId} />}></Route>
      <Route path={"alignments/*"} element={<BlastResultAlignmentsView {...commonProps} />}></Route>
      <Route path={"hitdetails/:hitNum/*"} element={<BlastResultHitDetailsView {...commonProps} />}></Route>
    </Routes>
  );
};

export default BlastResultDetailPage;
